import { apiClient } from '../../../app/api/apiClient';
import type { UserApiOrdersPage, UserApiUser } from '../types/user.types';

export interface CreateUserPayload {
  telegram_id: number;
  full_name?: string;
  username?: string;
  phone_number?: string;
  tenant_id: number;
  is_blocked: boolean;
}

export interface UpdateUserPayload {
  full_name?: string | null;
  username?: string | null;
  phone_number?: string | null;
  tenant_id?: number;
  is_blocked?: boolean;
}

export interface WalletAdjustmentPayload {
  amount: number;
  reason: string;
}

export interface WalletAdjustmentResponse {
  success: boolean;
  wallet: { balance: number };
  transaction: Record<string, unknown>;
}

interface UsersResponse {
  success: boolean;
  users: UserApiUser[];
  meta: {
    page: number;
    limit: number;
    total: number;
  };
}

interface UserResponse {
  success: boolean;
  user: UserApiUser;
}

export interface UsersPage {
  users: UserApiUser[];
  meta: UsersResponse['meta'];
}

export async function getUsers(page = 1, pageSize = 20, search = ''): Promise<UsersPage> {
  const response = await apiClient.get('/api/users', {
    query: { page, page_size: pageSize, ...(search.trim() ? { search: search.trim() } : {}) },
  });
  const data = response.data as UsersResponse;
  return { users: data.users, meta: data.meta };
}

export async function getTenantUsers(
  tenantId: number,
  page = 1,
  pageSize = 20,
  search = '',
): Promise<UsersPage> {
  const response = await apiClient.get(`/api/super-admin/tenants/${tenantId}/users`, {
    query: { page, page_size: pageSize, ...(search.trim() ? { search: search.trim() } : {}) },
  });
  const data = response.data as UsersResponse;
  return { users: data.users, meta: data.meta };
}

export async function getUser(id: string): Promise<UserApiUser> {
  const response = await apiClient.get(`/api/users/${id}`);
  return (response.data as UserResponse).user;
}

export async function createUser(payload: CreateUserPayload): Promise<UserApiUser> {
  const response = await apiClient.post('/api/users', payload);
  return (response.data as UserResponse).user;
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<UserApiUser> {
  const response = await apiClient.patch(`/api/users/${id}`, payload);
  return (response.data as UserResponse).user;
}

export async function creditUserWallet(
  id: string,
  payload: WalletAdjustmentPayload,
) {
  const response = await apiClient.post(`/api/super-admin/wallets/${id}/credit`, payload);
  return response.data as WalletAdjustmentResponse;
}

export async function debitUserWallet(
  id: string,
  payload: WalletAdjustmentPayload,
) {
  const response = await apiClient.post(`/api/super-admin/wallets/${id}/debit`, payload);
  return response.data as WalletAdjustmentResponse;
}

export async function getUserOrders(
  id: string,
  page = 1,
  pageSize = 5,
): Promise<UserApiOrdersPage> {
  const response = await apiClient.get(`/api/users/${id}/orders`, {
    query: { page, page_size: pageSize },
  });
  return response.data as UserApiOrdersPage;
}
